import React, { useState, useEffect } from 'react';
import FormattedMessage from '../components/FormattedMessage';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { aiAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  Sparkles, 
  MapPin, 
  Users, 
  Calendar,
  Dog,
  DollarSign,
  Car,
  GraduationCap,
  ShoppingBag,
  Loader2,
  ArrowRight,
  CheckCircle2,
  Save,
  Building2,
  Globe,
  ChevronDown
} from 'lucide-react';

// Easy Date Picker Component
function EasyDatePicker({ value, onChange, className }) {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  
  // Parse existing value or use today
  const parseDate = (dateStr) => {
    if (!dateStr) return { month: currentMonth, year: currentYear, day: today.getDate() };
    const d = new Date(dateStr);
    return { month: d.getMonth(), year: d.getFullYear(), day: d.getDate() };
  };
  
  const [selected, setSelected] = useState(parseDate(value));
  
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Generate years (current year + 2 years ahead)
  const years = Array.from({ length: 3 }, (_, i) => currentYear + i);
  
  // Generate days based on selected month/year
  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const days = Array.from({ length: getDaysInMonth(selected.month, selected.year) }, (_, i) => i + 1);
  
  const handleChange = (field, val) => {
    const newSelected = { ...selected, [field]: parseInt(val) };
    
    // Adjust day if it exceeds the new month's days
    const maxDays = getDaysInMonth(newSelected.month, newSelected.year);
    if (newSelected.day > maxDays) newSelected.day = maxDays;
    
    setSelected(newSelected);
    
    // Format as YYYY-MM-DD
    const dateStr = `${newSelected.year}-${String(newSelected.month + 1).padStart(2, '0')}-${String(newSelected.day).padStart(2, '0')}`;
    onChange(dateStr);
  };
  
  return (
    <div className={`flex gap-2 ${className}`}>
      {/* Month */}
      <Select value={String(selected.month)} onValueChange={(v) => handleChange('month', v)}>
        <SelectTrigger className="bg-white border-slate-300 text-slate-800 flex-1">
          <SelectValue placeholder="Month" />
        </SelectTrigger>
        <SelectContent className="bg-white border-slate-300 max-h-60">
          {months.map((m, i) => (
            <SelectItem key={i} value={String(i)} className="text-slate-700">{m}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      {/* Day */}
      <Select value={String(selected.day)} onValueChange={(v) => handleChange('day', v)}>
        <SelectTrigger className="bg-white border-slate-300 text-slate-800 w-20">
          <SelectValue placeholder="Day" />
        </SelectTrigger>
        <SelectContent className="bg-white border-slate-300 max-h-60">
          {days.map((d) => (
            <SelectItem key={d} value={String(d)} className="text-slate-700">{d}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      {/* Year */}
      <Select value={String(selected.year)} onValueChange={(v) => handleChange('year', v)}>
        <SelectTrigger className="bg-white border-slate-300 text-slate-800 w-24">
          <SelectValue placeholder="Year" />
        </SelectTrigger>
        <SelectContent className="bg-white border-slate-300">
          {years.map((y) => (
            <SelectItem key={y} value={String(y)} className="text-slate-700">{y}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Funny save profile prompts
const funnyPrompts = [
  "Save your preferences so we don't have to play 20 questions again! 🎯",
  "Your future self will thank you for saving this. Trust us. ✨",
  "Don't let your perfect settings vanish like your WiFi signal at the barracks! 📡",
  "Save now, or explain to your spouse why you forgot everything... again. 😅",
  "These preferences are chef's kiss. Lock them in! 👨‍🍳",
  "PCS stress is real. At least your housing preferences don't have to be. 💪",
  "Save this profile faster than you signed your PCS orders! ✍️",
  "Your search history called - it wants you to save your preferences. 📞",
  "Because nobody wants to fill out forms twice. Not even in the military. 🎖️",
  "Pro tip: Saved preferences = More time for exploring Christmas markets! 🎄",
];

export default function LocationMatchPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [savePromptText, setSavePromptText] = useState('');
  const [profileSaved, setProfileSaved] = useState(false);

  const [formData, setFormData] = useState({
    location_type: '', // 'base' or 'city'
    location_name: '',
    arrival_date: '',
    family_size: 1,
    pets: false,
    budget_band: '',
    priorities: {
      commute: 50,
      schools: 50,
      shopping: 50,
      nightlife: 30,
      nature: 50,
    },
  });

  // Military bases in Europe
  const militaryBases = [
    { value: 'ramstein', label: 'Ramstein Air Base', region: 'Rhineland-Palatinate' },
    { value: 'spangdahlem', label: 'Spangdahlem Air Base', region: 'Rhineland-Palatinate' },
    { value: 'patch', label: 'Patch Barracks', region: 'Stuttgart' },
    { value: 'panzer', label: 'Panzer Kaserne', region: 'Stuttgart' },
    { value: 'clay', label: 'Clay Kaserne', region: 'Wiesbaden' },
    { value: 'grafenwoehr', label: 'Grafenwöhr Training Area', region: 'Bavaria' },
    { value: 'vilseck', label: 'Rose Barracks (Vilseck)', region: 'Bavaria' },
    { value: 'ansbach', label: 'Katterbach Kaserne', region: 'Ansbach' },
    { value: 'wiesbaden', label: 'Lucius D. Clay Kaserne', region: 'Wiesbaden' },
    { value: 'aviano', label: 'Aviano Air Base', region: 'Italy' },
    { value: 'naples', label: 'NSA Naples', region: 'Italy' },
    { value: 'rota', label: 'Naval Station Rota', region: 'Spain' },
    { value: 'lakenheath', label: 'RAF Lakenheath', region: 'UK' },
    { value: 'mildenhall', label: 'RAF Mildenhall', region: 'UK' },
  ];

  // European cities/regions
  const europeanCities = [
    { value: 'frankfurt', label: 'Frankfurt', region: 'Hesse, Germany' },
    { value: 'munich', label: 'Munich', region: 'Bavaria, Germany' },
    { value: 'heidelberg', label: 'Heidelberg', region: 'Baden-Württemberg, Germany' },
    { value: 'kaiserslautern', label: 'Kaiserslautern', region: 'Rhineland-Palatinate, Germany' },
    { value: 'stuttgart', label: 'Stuttgart', region: 'Baden-Württemberg, Germany' },
    { value: 'berlin', label: 'Berlin', region: 'Germany' },
    { value: 'cologne', label: 'Cologne', region: 'North Rhine-Westphalia, Germany' },
    { value: 'rome', label: 'Rome', region: 'Italy' },
    { value: 'milan', label: 'Milan', region: 'Italy' },
    { value: 'madrid', label: 'Madrid', region: 'Spain' },
    { value: 'barcelona', label: 'Barcelona', region: 'Spain' },
    { value: 'london', label: 'London', region: 'UK' },
    { value: 'brussels', label: 'Brussels', region: 'Belgium' },
    { value: 'amsterdam', label: 'Amsterdam', region: 'Netherlands' },
  ];

  // Get random funny prompt when results are shown
  useEffect(() => {
    if (step === 4 && !profileSaved) {
      const randomPrompt = funnyPrompts[Math.floor(Math.random() * funnyPrompts.length)];
      setSavePromptText(randomPrompt);
      // Show save prompt after 2 seconds
      const timer = setTimeout(() => setShowSavePrompt(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [step, profileSaved]);

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/location-match');
      return;
    }

    setLoading(true);
    try {
      // Normalize priorities to 0-1 scale
      const normalizedPriorities = {};
      Object.entries(formData.priorities).forEach(([key, value]) => {
        normalizedPriorities[key] = value / 100;
      });

      // Get the display name for the location
      const locationLabel = formData.location_type === 'base'
        ? militaryBases.find(b => b.value === formData.location_name)?.label
        : europeanCities.find(c => c.value === formData.location_name)?.label;

      const response = await aiAPI.locationMatch({
        base_name: locationLabel || formData.location_name,
        arrival_date: formData.arrival_date,
        family_size: formData.family_size,
        pets: formData.pets,
        budget_band: formData.budget_band,
        priorities: normalizedPriorities,
      });

      setResults(response.data);
      setStep(4);
    } catch (error) {
      console.error('AI Match Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = () => {
    // In a real app, this would save to the backend
    // For now, save to localStorage
    const profileData = {
      ...formData,
      savedAt: new Date().toISOString(),
      userId: user?.id,
    };
    localStorage.setItem('tla_user_preferences', JSON.stringify(profileData));
    setProfileSaved(true);
    setShowSavePrompt(false);
  };

  const renderStep1 = () => (
    <div className="space-y-6 animate-fade-in">
      {/* Location Type Selection */}
      <div>
        <Label className="text-slate-700 mb-3 block">Where will you be located?</Label>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setFormData({ ...formData, location_type: 'base', location_name: '' })}
            className={`p-4 rounded-sm border flex flex-col items-center gap-2 transition-all ${
              formData.location_type === 'base'
                ? 'bg-signal-blue/10 border-signal-blue text-signal-blue'
                : 'bg-white border-slate-300 text-slate-600 hover:border-slate-600'
            }`}
            data-testid="location-type-base"
          >
            <Building2 className="w-6 h-6" />
            <span className="text-sm font-medium">Military Base</span>
          </button>
          <button
            onClick={() => setFormData({ ...formData, location_type: 'city', location_name: '' })}
            className={`p-4 rounded-sm border flex flex-col items-center gap-2 transition-all ${
              formData.location_type === 'city'
                ? 'bg-military-gold/10 border-military-gold text-military-gold'
                : 'bg-white border-slate-300 text-slate-600 hover:border-slate-600'
            }`}
            data-testid="location-type-city"
          >
            <Globe className="w-6 h-6" />
            <span className="text-sm font-medium">City / Region</span>
          </button>
        </div>
      </div>

      {/* Location Selector - Shows based on type */}
      {formData.location_type && (
        <div className="animate-fade-in">
          <Label className="text-slate-700 mb-2 block">
            {formData.location_type === 'base' ? 'Select your base' : 'Select your city or region'}
          </Label>
          <Select
            value={formData.location_name}
            onValueChange={(value) => setFormData({ ...formData, location_name: value })}
          >
            <SelectTrigger className="bg-white border-slate-300 text-slate-800" data-testid="location-select">
              <MapPin className="w-4 h-4 mr-2 text-slate-800" />
              <SelectValue placeholder={formData.location_type === 'base' ? 'Choose a base...' : 'Choose a city...'} />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-300 max-h-64">
              {(formData.location_type === 'base' ? militaryBases : europeanCities).map((loc) => (
                <SelectItem key={loc.value} value={loc.value} className="text-slate-700">
                  <div className="flex flex-col">
                    <span>{loc.label}</span>
                    <span className="text-xs text-slate-500">{loc.region}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label className="text-slate-700 mb-2 block flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-600" />
          Expected arrival date (optional)
        </Label>
        <EasyDatePicker
          value={formData.arrival_date}
          onChange={(date) => setFormData({ ...formData, arrival_date: date })}
          data-testid="arrival-date"
        />
        <p className="text-xs text-slate-800 mt-1">Select month, day, and year</p>
      </div>

      <Button
        onClick={() => setStep(2)}
        disabled={!formData.location_type || !formData.location_name}
        className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white rounded-sm"
        data-testid="next-step-1"
      >
        Continue
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6 animate-fade-in">
      <div>
        <Label className="text-slate-700 mb-2 block">How many people in your household?</Label>
        <div className="flex items-center gap-4">
          <Users className="w-5 h-5 text-slate-800" />
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5, '6+'].map((num) => (
              <button
                key={num}
                onClick={() => setFormData({ ...formData, family_size: typeof num === 'number' ? num : 6 })}
                className={`w-10 h-10 rounded-sm border ${
                  formData.family_size === (typeof num === 'number' ? num : 6)
                    ? 'bg-signal-blue border-signal-blue text-white'
                    : 'bg-white border-slate-300 text-slate-600 hover:border-slate-600'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <Label className="text-slate-700 mb-2 block">Do you have pets?</Label>
        <div className="flex gap-2">
          <button
            onClick={() => setFormData({ ...formData, pets: false })}
            className={`flex-1 py-3 px-4 rounded-sm border flex items-center justify-center gap-2 ${
              !formData.pets
                ? 'bg-signal-blue/10 border-signal-blue text-signal-blue'
                : 'bg-white border-slate-300 text-slate-600'
            }`}
            data-testid="pets-no"
          >
            No Pets
          </button>
          <button
            onClick={() => setFormData({ ...formData, pets: true })}
            className={`flex-1 py-3 px-4 rounded-sm border flex items-center justify-center gap-2 ${
              formData.pets
                ? 'bg-military-gold/10 border-military-gold text-military-gold'
                : 'bg-white border-slate-300 text-slate-600'
            }`}
            data-testid="pets-yes"
          >
            <Dog className="w-4 h-4" />
            Yes, Pets
          </button>
        </div>
      </div>

      <div>
        <Label className="text-slate-700 mb-2 block">Monthly budget range</Label>
        <Select
          value={formData.budget_band}
          onValueChange={(value) => setFormData({ ...formData, budget_band: value })}
        >
          <SelectTrigger className="bg-white border-slate-300 text-slate-800" data-testid="budget-select">
            <DollarSign className="w-4 h-4 mr-2 text-slate-800" />
            <SelectValue placeholder="Select budget range" />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-300">
            <SelectItem value="under-1500" className="text-slate-700">Under €1,500</SelectItem>
            <SelectItem value="1500-2000" className="text-slate-700">€1,500 - €2,000</SelectItem>
            <SelectItem value="2000-2500" className="text-slate-700">€2,000 - €2,500</SelectItem>
            <SelectItem value="2500-3000" className="text-slate-700">€2,500 - €3,000</SelectItem>
            <SelectItem value="over-3000" className="text-slate-700">Over €3,000</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={() => setStep(1)}
          className="flex-1 border-slate-300 text-slate-600"
        >
          Back
        </Button>
        <Button
          onClick={() => setStep(3)}
          className="flex-1 bg-signal-blue hover:bg-signal-blue-hover text-white rounded-sm"
          data-testid="next-step-2"
        >
          Continue
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6 animate-fade-in">
      <p className="text-slate-600 text-sm mb-4">
        Adjust the sliders to indicate your priorities. Higher values mean more important.
      </p>

      {[
        { key: 'commute', label: 'Short Commute', icon: Car },
        { key: 'schools', label: 'Good Schools', icon: GraduationCap },
        { key: 'shopping', label: 'Shopping & Amenities', icon: ShoppingBag },
        { key: 'nightlife', label: 'Restaurants & Nightlife', icon: Sparkles },
        { key: 'nature', label: 'Parks & Nature', icon: MapPin },
      ].map(({ key, label, icon: Icon }) => (
        <div key={key}>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-slate-700 flex items-center gap-2">
              <Icon className="w-4 h-4 text-slate-500" />
              {label}
            </Label>
            <span className="text-sm text-signal-blue font-mono">
              {formData.priorities[key]}%
            </span>
          </div>
          <Slider
            value={[formData.priorities[key]]}
            onValueChange={([value]) =>
              setFormData({
                ...formData,
                priorities: { ...formData.priorities, [key]: value },
              })
            }
            max={100}
            step={10}
            className="w-full"
          />
        </div>
      ))}

      <div className="flex gap-3 pt-4">
        <Button
          variant="outline"
          onClick={() => setStep(2)}
          className="flex-1 border-slate-300 text-slate-600"
        >
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          className="flex-1 bg-military-gold hover:bg-military-gold/90 text-slate-950 rounded-sm"
          data-testid="find-match"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Finding Matches...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Find My Match
            </>
          )}
        </Button>
      </div>
    </div>
  );

  const renderResults = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-emerald-400" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-slate-800 mb-2">
          YOUR TOP MATCHES
        </h2>
        <p className="text-slate-600">
          Based on your preferences, here are the best locations for you:
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 rounded-sm">
        <FormattedMessage content={results?.response || 'No recommendations available.'} dark={false} />
        {results?.provider && (
          <p className="text-xs text-slate-800 mt-4 flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            Powered by {results.provider}
          </p>
        )}
      </div>

      {/* Funny Save Profile Prompt */}
      {showSavePrompt && !profileSaved && (
        <div className="bg-gradient-to-r from-military-gold/20 to-signal-blue/20 border border-military-gold/30 rounded-sm p-4 animate-fade-in">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-military-gold/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Save className="w-5 h-5 text-military-gold" />
            </div>
            <div className="flex-1">
              <p className="text-slate-700 text-sm mb-3">{savePromptText}</p>
              <Button
                onClick={handleSaveProfile}
                size="sm"
                className="bg-military-gold hover:bg-military-gold/90 text-slate-950"
                data-testid="save-profile-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                Save My Preferences
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Saved Confirmation */}
      {profileSaved && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-sm p-4 animate-fade-in">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <p className="text-emerald-300 text-sm">
              Preferences saved! We'll remember you next time. 🎉
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={() => {
            setStep(1);
            setResults(null);
            setShowSavePrompt(false);
            setProfileSaved(false);
          }}
          className="flex-1 border-slate-300 text-slate-600"
        >
          Start Over
        </Button>
        <Button
          onClick={() => navigate('/listings')}
          className="flex-1 bg-signal-blue hover:bg-signal-blue-hover text-white rounded-sm"
        >
          Browse Properties
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="location-match-page">
      <div className="max-w-xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-military-gold/10 border border-military-gold/30 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-military-gold" />
            <span className="text-military-gold text-sm">Smart Matching</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-800 mb-2">
            FIND YOUR PERFECT LOCATION
          </h1>
          <p className="text-slate-600">
            Answer a few questions and we'll find the best neighborhood for your family.
          </p>
        </div>

        {/* Progress Steps */}
        {step < 4 && (
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`w-3 h-3 rounded-full ${
                  s === step
                    ? 'bg-signal-blue'
                    : s < step
                    ? 'bg-emerald-500'
                    : 'bg-slate-700'
                }`}
              />
            ))}
          </div>
        )}

        {/* Form Card */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sm:p-8">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderResults()}
        </div>
      </div>
    </div>
  );
}
